import isEqual from 'lodash/isEqual';

import {
  getIntersectCordsBetweenLines,
  getMousePositionOnShape,
  getPointField,
  getPointDistance,
} from "../../../../../../helpers/sqftHelper";
import sqftShapeManager from "../../../../store/sqftShapeManager";
import {SHAPE_PART_ID, SHAPE_ROTATE_DIRECTION} from "../../../../../../constants";
import SqftStore from "../../../../store/sqftStore";
import ShapeHelper from "../../../../../../helpers/shapeHelper";
import SeamToolRender from "./seamToolRender";
import SlabWorkspace from "../../../slab/slabWorkspace";
import SlabAdditionalShape from "../../../slab/slabAdditionalShape";
import {
  cleanPolygonPoints,
  getHorizontalLineIntersectionPoints, getLineByPoint, getSnappedAnglePoint,
  getVerticalLineIntersectionPoints, isPointOnLineSegment,
  splitShapeByIntersection
} from "./seamToolHelper";
import {createShapeFromPoints} from "../../../../../../helpers/freeDrawHelper";
import workspace from "../../../workspace";
import {getClosestPointOnLine} from "../../../../../../helpers/coordinatesHelper";
import {getSeamCutPoint} from "../../../../../../helpers/pointsHelper";
import PointAndLine from "../models/PointAndLine";

class SeamTool {
  __shape;
  seamToolRender;

  constructor(_shape) {
    this.__shape = _shape;
    this.seamToolRender = new SeamToolRender(this);
  }

  getMaxLength = () => {
    //todo
    return 10
  }

  /**
   * Checks if a free seam point can be added at the specified location
   * @returns {{canAdd: boolean, snapPoint: Object, reason?: string} | null}
   */
  canAddFreeSeamPoint = (mousePos, hoveredLine) => {
    const shape = hoveredLine.getParent();
    const mousePosOnShape = getMousePositionOnShape(mousePos.x, mousePos.y, shape);

    const lineStart = hoveredLine.from;
    const lineEnd = hoveredLine.to;
    let seamCutPos = mousePosOnShape;
    if (shape.seamCutPointsAndLines[0]?.point) {
      seamCutPos = getSnappedAnglePoint(shape.seamCutPointsAndLines[0]?.point, mousePosOnShape)
    }

    seamCutPos = getClosestPointOnLine(lineStart, lineEnd, seamCutPos);
    const lines = shape.getLines();

    // First point OR starting fresh after completing a seam - can always be added
    if (!shape.seamCutPointsAndLines[0]?.point || shape.seamCutPointsAndLines.length >= 2) {
      return {
        canAdd: true,
        snapPoint: seamCutPos
      };
    }

    // 1. Check: point is not on the same line
    const firstPointLine = getLineByPoint(lines, shape.seamCutPointsAndLines[0].point);
    if (firstPointLine?.id === hoveredLine.id) {
      return {
        canAdd: false,
        snapPoint: seamCutPos,
        reason: 'same_line'
      };
    }

    // 2. Check: line does not cross shape boundaries
    const firstPoint = shape.seamCutPointsAndLines[0].point;

    const firstPointHostLines = lines.filter(l =>
      isPointOnLineSegment(firstPoint, l.from, l.to, 0.5)
    );

    const secondPointHostLines = lines.filter(l =>
      isPointOnLineSegment(seamCutPos, l.from, l.to, 0.5)
    );

    const skipIds = new Set([
      ...firstPointHostLines.map(l => l.id),
      ...secondPointHostLines.map(l => l.id),
    ]);

    for (const testLine of lines) {
      if (skipIds.has(testLine.id)) continue;

      const intersection = getIntersectCordsBetweenLines(
        firstPoint,
        seamCutPos,
        testLine.from,
        testLine.to
      );

      if (intersection) {
        const isAtVertex =
          isPointOnLineSegment(intersection, testLine.from, testLine.to, 0.5) &&
          (getPointDistance(intersection, testLine.from) < 0.5 || getPointDistance(intersection, testLine.to) < 0.5);

        if (!isAtVertex) {
          // return {
          //   canAdd: false,
          //   snapPoint: seamCutPos,
          //   reason: 'intersects_boundary'
          // };
          // Return null - don't show anything for boundary crossing
          return null;
        }
      }
    }

    return {
      canAdd: true,
      snapPoint: seamCutPos
    };
  };

  addFreeSeamPoint = (mousePos, hoveredLine) => {
    const shape = hoveredLine.getParent();
    const validation = this.canAddFreeSeamPoint(mousePos, hoveredLine);

    if (!validation.canAdd) {
      return; // Don't add point
    }

    const seamCutPoint = getSeamCutPoint(validation.snapPoint, shape);

    if (shape.seamCutPointsAndLines.length >= 2) {
      // Clear all existing points
      sqftShapeManager.resetFreeSeamPoints(shape);
    }

    shape.seamCutPointsAndLines.push(new PointAndLine(seamCutPoint, hoveredLine));
  };

  /**
   *  Cut shape into two parts
   */
  addFreeSeam = () => {
    const linesWithoutAnglePoints = this.__shape.createLinesWithoutAngle();
    const intersectionPointAndLine1 = this.__shape.seamCutPointsAndLines[0];
    const intersectionPointAndLine2 = this.__shape.seamCutPointsAndLines[1];

    if (intersectionPointAndLine1 && intersectionPointAndLine2) {
      let seamShapesPoints = this.splitPolygonBySegment(linesWithoutAnglePoints, intersectionPointAndLine1.point, intersectionPointAndLine2.point);
      //let seamShapesPoints = splitShapeByIntersection(linesWithoutAnglePoints, [intersectionPointAndLine1.point, intersectionPointAndLine2.point]);
      void splitShapeByIntersection // this is second version


      this.createShapesFromSeamPointsAndSetSlabRotation(seamShapesPoints);
      this.destroyAllOddSeamCutPoints();
      sqftShapeManager.resetFreeSeamPoints(this.__shape);

      this.__shape.deregisterEvents();
      SqftStore.addSeamHistory(this.__shape);
    } else {
      console.log("getHorizontalLineIntersectionPoints error missing intersections");
    }
  }

  //todo
  addSeamPieceHorizontal = (mousePos) => {
    // const mousePosOnFiled = getMousePositionOnField(mousePos.x, mousePos.y);
    // const serverState = store.getState().serverState;
    // const pos = this.__shape.getPosition();
    //let intersectionPoint1 = {x: -serverState.windowWidth, y: mousePosOnFiled.y - pos.y};
    //let intersectionPoint2 = {x: serverState.windowWidth, y: mousePosOnFiled.y - pos.y};

    let absoluteLinePoint1Pos = this.seamToolRender.absoluteLinePoint1Pos;
    let toPoint = {...mousePos};
    if (absoluteLinePoint1Pos && absoluteLinePoint1Pos.y) {
        let pf = getPointField(absoluteLinePoint1Pos.x, absoluteLinePoint1Pos.y, this.__shape);
        toPoint.y = pf.y;
    }
    const lines = this.__shape.getLines();
    console.log("linesPoints", lines.map((line) => { return {x: line.from.x, y: line.from.y}}));

    const linesWithoutAnglePoints = this.__shape.createLinesWithoutAngle();
    let [intersectionPointAndLine1, intersectionPointAndLine2] = getHorizontalLineIntersectionPoints(toPoint, this.__shape, linesWithoutAnglePoints);
    if (intersectionPointAndLine1 && intersectionPointAndLine2) {
      let [p1, p2] = this.splitPolygonBySegment(linesWithoutAnglePoints,
        intersectionPointAndLine1.intersectionCords,
        intersectionPointAndLine2.intersectionCords,
      );
      //console.log("piece1Points before clean", p1.map(point => { return {x: point.x, y: point.y}}));
      //console.log("piece2Points before clean", p2.map(point => { return {x: point.x, y: point.y}}));

      p1 = cleanPolygonPoints(p1,{});
      p2 = cleanPolygonPoints(p2,{});

      //console.log("intersectionPoint1", intersectionPointAndLine1);
      //console.log("intersectionPoint2", intersectionPointAndLine2);

      //console.log("linesWithoutAnglePoints", linesWithoutAnglePoints.map((line) => { return {x: line.from.x, y: line.from.y}}));
      //console.log("piece1Points", p1.map(point => { return {x: point.x, y: point.y}}));
      //console.log("piece2Points", p2.map(point => { return {x: point.x, y: point.y}}));
      this.createShapesFromSeamPointsAndSetSlabRotation([p1, p2]);

      this.__shape.deregisterEvents();
      //this.__shape.shapeSlab.setRotationDegreesSlab(0); //? not needed
      SqftStore.addSeamHistory(this.__shape);
    } else {
      console.log("getHorizontalLineIntersectionPoints error missing intersections");
    }
  }

  createShapesFromSeamPointsAndSetSlabRotation = (seamShapesPoints) => {
    let parentShapeCenter = this.__shape.getCenter();

    for (let seamPoints of seamShapesPoints) {
      let isBacksplashPiece = [SHAPE_PART_ID.backsplashPiece, SHAPE_PART_ID.waterfallPiece].includes(this.__shape.partId);
      let partId = SHAPE_PART_ID.seamPiece;
      seamPoints = this.preparePointCords(seamPoints);
      let shapePieceId = createShapeFromPoints(seamPoints, partId, true);
      let shapePiece = sqftShapeManager.getShapeById(shapePieceId);
      shapePiece.featureId = this.__shape.featureId;
      shapePiece.featureName = this.__shape.featureName;
      shapePiece.colorSet = this.__shape.colorSet;
      shapePiece.deregisterPartEvents();
      let rotatedDiffPos = {x: 0, y: 0};

      if (isBacksplashPiece) {
        //add additional backsplash to slabWorkspace
        let parentSlabAdditionalShape = SlabWorkspace.slabAdditionalShapes.findOneByBacksplashShapeId(this.__shape.id);
        let newSlabAdditionalShape = new SlabAdditionalShape(parentSlabAdditionalShape.line, shapePieceId);
        SlabWorkspace.slabAdditionalShapes.addActiveBacksplash(newSlabAdditionalShape);
      }

      shapePiece.shapeSlab.setSlabPos(Object.assign({}, this.__shape.slabPos));
      if (this.__shape.rotatedDegreesSlab !== 0) {

        // this will rotate sqft and slab shape points to 0 it is required for correct rotation and drawing images in the right part
        let shapePieceCenterPointBeforeRotation = ShapeHelper.getCenterPoint(shapePiece.getLines().map(line => line.from));
        let reqRotatedDegreesToZero = 360 - this.__shape.rotatedDegreesSlab;
        //console.log("piece before rotate1", shapePiece.getLines().map((line) => { return {x: line.from.x, y: line.from.y}}));
        shapePiece.rotate(reqRotatedDegreesToZero, SHAPE_ROTATE_DIRECTION.left, true, parentShapeCenter);
        //console.log("piece rotate2", shapePiece.getLines().map((line) => { return {x: line.from.x, y: line.from.y}}));
        shapePiece.rotate(360, SHAPE_ROTATE_DIRECTION.left, false, parentShapeCenter);
        //console.log("piece rotate3", shapePiece.getLines().map((line) => { return {x: line.from.x, y: line.from.y}}));
        shapePiece.setRotationDegrees(0);
        shapePiece.shapeSlab.setRotationDegreesSlab(0);

        //rotate shape and move it to diff position (this is for correct displaying right part)
        shapePiece.rotate(this.__shape.rotatedDegreesSlab, SHAPE_ROTATE_DIRECTION.left, true);
        let shapePieceCenterPointAfterRotation = ShapeHelper.getCenterPoint(shapePiece.getLines().map(line => line.from));
        this.__shape.setIsPointsChanged(false); // prevent points change after rotation
        console.log("piece rotate4", shapePiece.getLines().map((line) => { return {x: line.from.x, y: line.from.y}}));

        rotatedDiffPos = {
          x: shapePieceCenterPointBeforeRotation.x - shapePieceCenterPointAfterRotation.x,
          y: shapePieceCenterPointBeforeRotation.y - shapePieceCenterPointAfterRotation.y,
        }
      }
      let coreShapeAbsolutePosition = this.__shape.getSlabAbsolutePosition()
      let slabAbsolutePosition = coreShapeAbsolutePosition ? coreShapeAbsolutePosition : this.__shape.getSlabPosition();
      shapePiece.slabPos = {
        x: slabAbsolutePosition.x + rotatedDiffPos.x,
        y: slabAbsolutePosition.y + rotatedDiffPos.y,
      }

      shapePiece.shapeSlab.setParentId(this.__shape.id); //this is not needed  maybe in future
      shapePiece.setPosition(Object.assign({}, this.__shape.getRegularPosition()));

      this.__shape.addShapePieceId(shapePiece.id);
    }
  };

  addSeamPieceVertical = (mousePos) => {
    // const mousePosOnFiled = getMousePositionOnField(mousePos.x, mousePos.y);
    // const serverState = store.getState().serverState;
    // const pos = this.__shape.getPosition();
    //
    // let intersectionPoint1 = {x: mousePosOnFiled.x - pos.x, y: -serverState.windowHeight};
    // let intersectionPoint2 = {x: mousePosOnFiled.x - pos.x, y: serverState.windowHeight};
    let absoluteLinePoint1Pos = this.seamToolRender.absoluteLinePoint1Pos;
    let toPoint = {...mousePos};
    if (absoluteLinePoint1Pos && absoluteLinePoint1Pos.x) {
      let pf = getPointField(absoluteLinePoint1Pos.x, absoluteLinePoint1Pos.y, this.__shape);
      toPoint.x = pf.x;
    }
    const linesWithoutAnglePoints = this.__shape.createLinesWithoutAngle();
    let [intersectionPointAndLine1, intersectionPointAndLine2] = getVerticalLineIntersectionPoints(toPoint, this.__shape, linesWithoutAnglePoints);

    if (intersectionPointAndLine1 && intersectionPointAndLine2) {
      let [p1, p2] = this.splitPolygonBySegment(linesWithoutAnglePoints,
        intersectionPointAndLine1.intersectionCords,
        intersectionPointAndLine2.intersectionCords,
      );

      p1 = cleanPolygonPoints(p1,{});
      p2 = cleanPolygonPoints(p2,{});

      // console.log("intersectionPoint1", intersectionPointAndLine1);
      // console.log("intersectionPoint2", intersectionPointAndLine2);
      // console.log("shapePoints", this.__shape.getLines().map((line) => { return {x: line.from.x, y: line.from.y}}));
      // console.log("piece1Points", seamShapesPoints[0].map(point => { return {x: point.x, y: point.y}}));
      // console.log("piece2Points", seamShapesPoints[1].map(point => { return {x: point.x, y: point.y}}));
      this.createShapesFromSeamPointsAndSetSlabRotation([p1, p2]);

      this.__shape.deregisterEvents();
      //this.__shape.shapeSlab.setRotationDegreesSlab(0); //? not needed
      SqftStore.addSeamHistory(this.__shape);
    } else {
      console.log("getVerticalLineIntersectionPoints error missing intersections");
    }
  };

  destroyAllSeamCutPoints = () => {
    const shapes = workspace.getShapesWithoutAttachedResources();
    for (const shape of shapes) {
      if (shape.seamCutPointsAndLines && shape.seamCutPointsAndLines.length) {
        for (const seamCutPointsAndLine of shape.seamCutPointsAndLines) {
          seamCutPointsAndLine.destroy();
        }
        shape.seamCutPointsAndLines = []
      }
    }
  }

  destroyAllOddSeamCutPoints = () => {
    const shapes = workspace.getShapesWithoutAttachedResources();
    for (const shape of shapes) {
      if (shape.seamCutPointsAndLines && shape.seamCutPointsAndLines.length) {
        sqftShapeManager.resetSingleSeamPointIfOdd(shape);
      }
    }
  }

  preparePointCords = (seamPoints) => {
    return seamPoints.map((seamPoint) => {
      seamPoint.x = Math.round10(seamPoint.x, -1);
      seamPoint.y = Math.round10(seamPoint.y, -1);
      return seamPoint
    })
  };

  /**
   * @deprecated
   * @param {[]} lines
   * @param {*} intersectionPointAndLine1
   * @param {*} intersectionPointAndLine2
   * @param {string} splitOnlyOrientation
   */
  splitShape = (lines, intersectionPointAndLine1, intersectionPointAndLine2, splitOnlyOrientation) => {
    let piece1Points = [];
    let piece2Points = [];
    let intersectionNumber = 1;
    let lastPoint;
    let intersectionPoint1 = intersectionPointAndLine1.intersectionCords
    let intersectionPoint2 = intersectionPointAndLine2.intersectionCords

    lines.forEach((line) => {
      let intersectionCords = null;
      void splitOnlyOrientation;

      // version 1.0 search cords again
      //if (line.getLineDirection().orientation === splitOnlyOrientation || !splitOnlyOrientation) {
        // intersectionCords = getIntersectCords(
        //   Math.round10(line.from.x, -1), Math.round10(line.from.y, -1),
        //   Math.round10(line.to.x, -1), Math.round10(line.from.y, -1),
        //   Math.round10(intersectionPoint1.x, -1), Math.round10(intersectionPoint1.y, -1),
        //   Math.round10(intersectionPoint2.x, -1), Math.round10(intersectionPoint2.y,-1),
        //   false
        // )
      //}

      // version 2.0 use existing intersection cords
      if ((line === intersectionPointAndLine1.line || line === intersectionPointAndLine2.line)) {
        intersectionCords = line === intersectionPointAndLine1.line
          ? intersectionPointAndLine1.intersectionCords
          : intersectionPointAndLine2.intersectionCords
      }

      if (intersectionCords) {
        let ip = (Math.round10(intersectionCords.x, -1) === Math.round10(intersectionPoint1.x, -1)
          && Math.round10(intersectionCords.y, -1) === Math.round10(intersectionPoint1.y, -1))
          ? intersectionPoint1 : intersectionPoint2
        if (piece2Points.length === 1) { // triangle shape
          piece2Points.push(line.from);
        }
        const nextLine = line.getParent().getNextLine(line, undefined, true);
        if (piece1Points.length === 1
          && (nextLine.id === intersectionPointAndLine1.line.id || nextLine.id === intersectionPointAndLine2.line.id)
        ) {
          piece1Points.push(line.to); // triangle shape
        }
        piece1Points.push(ip);
        piece2Points.push(ip);
      } else {
        if (intersectionNumber % 2 !== 0) {
          lastPoint = piece1Points[piece1Points.length - 1];
          if (!lastPoint || !lastPoint.id || lastPoint.id !== line.from.id) {
            piece1Points.push(line.from);
          }
          if (piece1Points[0] !== line.to) {
            piece1Points.push(line.to);
          }
        }
        if (intersectionNumber % 2 === 0) {
          lastPoint = piece2Points[piece2Points.length - 1];
          if (!lastPoint || !lastPoint.id || lastPoint.id !== line.from.id) {
            piece2Points.push(line.from);
          }
          if (piece2Points[0] !== line.to) {
            piece2Points.push(line.to);
          }
        }
      }

      if (intersectionCords) {
        intersectionNumber += 1;
      }
      //console.log("intersectionCords", intersectionCords);
    });
    //console.log("piece1Points before filter", piece1Points.map(point => { return {x: point.x, y: point.y}}));
    //console.log("piece2Points before filter", piece2Points.map(point => { return {x: point.x, y: point.y}}));

    function filterEqualAndPointsOnTheSameLine(pointsArray) {
      return pointsArray.filter((point, index, array) => {
        let prev = array[index - 1];
        let next = array[index + 1];
        if (!prev) { // include first point
          prev = array[array.length - 1];
        }
        if (!next) {
          next = array[0]; // next point for the last point in array is the first point in array
        }
        const isEqualPoints = isEqual({x: point.x, y: point.y}, {x: prev.x, y: prev.y});
        const isOnTheSameLine = point.x === prev.x && point.x === next.x || point.y === prev.y && point.y === next.y
        return !isEqualPoints && !isOnTheSameLine
      })
    }

    return [filterEqualAndPointsOnTheSameLine(piece1Points), filterEqualAndPointsOnTheSameLine(piece2Points)]
  }

  /**
   * Robust split of a CW closed polygon by a segment pA–pB.
   * Works even if pA/pB are a bit off the boundary (snaps to the closest edge).
   */
  splitPolygonBySegment(lines, pA, pB, {
    eps = 1e-6,         // numeric tolerance for equality
    snapEps = 0.05,     // max distance to snap point to boundary (adjust to your units)
    debug = true
  } = {}) {

    // -------- small vector helpers --------
    const V = (x,y)=>({x,y});
    const cp = (p)=>V(p.x,p.y);
    const sub = (a,b)=>V(a.x-b.x, a.y-b.y);
    const add = (a,b)=>V(a.x+b.x, a.y+b.y);
    const mul = (a,t)=>V(a.x*t, a.y*t);
    const dot = (a,b)=>a.x*b.x + a.y*b.y;
    const len2 = (a)=>dot(a,a);
    const dist = (a,b)=>Math.hypot(a.x-b.x, a.y-b.y);
    const eq = (a,b)=>dist(a,b) <= eps;
    const clamp01 = (t)=> t<0?0 : (t>1?1:t);

    // -------- polygon vertices from lines --------
    const verts = lines.map(l => l.from);         // N vertices CW
    const N = verts.length;

    // Basic validation (helps catch wrong inputs fast)
    if (N < 3) throw new Error("Polygon must have at least 3 vertices");
    for (let i = 0; i < N; i++) {
      // check that lines are linked by shared point objects
      const n = (i + 1) % N;
      if (lines[i].to !== lines[n].from) {
        console.warn("Lines are not linked by shared objects at index", i, "->", n);
      }
    }

    // Distance from point to segment with closest point + param t in [0..1]
    function closestOnSeg(p, a, b) {
      const ab = sub(b,a);
      const denom = Math.max(eps, len2(ab));
      let t = dot(sub(p,a), ab) / denom;
      t = clamp01(t);
      const q = add(a, mul(ab, t));
      return { q, t, d: dist(p,q) };
    }

    // Find where a boundary point lies; snap to boundary if close enough.
    function locateOnBoundary(p, name="p") {
      // Snap to exact vertex if already very close
      for (let i=0;i<N;i++){
        if (eq(p, verts[i])) {
          if (debug) console.debug(`${name} snapped to vertex v${i}`);
          return { point: cp(verts[i]), atVertex: i, edge: (i-1+N)%N, t: 1 };
        }
      }

      // Otherwise search the closest edge
      let best = {edge:-1, t:0, q:null, d:Infinity};
      for (let i=0;i<N;i++){
        const a = verts[i], b = verts[(i+1)%N];
        const r = closestOnSeg(p,a,b);
        if (r.d < best.d) best = { edge:i, t:r.t, q:r.q, d:r.d };
      }

      if (debug) console.debug(`${name} -> nearest edge e${best.edge} (v${best.edge}->v${(best.edge+1)%N}), dist=${best.d}`);

      if (best.d <= snapEps) {
        // If very close to an endpoint, treat as vertex
        if (best.t <= eps) {
          const vi = best.edge;
          if (debug) console.debug(`${name} snapped to vertex v${vi}`);
          return { point: cp(verts[vi]), atVertex: vi, edge: (vi-1+N)%N, t:1 };
        }
        if (Math.abs(best.t-1) <= eps) {
          const vi = (best.edge+1)%N;
          if (debug) console.debug(`${name} snapped to vertex v${vi}`);
          return { point: cp(verts[vi]), atVertex: vi, edge: best.edge, t:1 };
        }
        // Inside the edge
        return { point: cp(best.q), atVertex: -1, edge: best.edge, t: best.t };
      }

      // Too far — tell the caller what went wrong
      const msg = `Intersection point ${name} is not on polygon boundary (minDist=${best.d}). 
Tip: increase {snapEps} or check your intersection computation.`;
      throw new Error(msg);
    }

    // Walk along polygon CW from X to Y and collect points
    function walkForward(from, to) {
      const pts = [cp(from.point)];

      let curV;
      if (from.atVertex !== -1) {
        curV = from.atVertex;             // start at this vertex, next is +1
      } else {
        curV = (from.edge + 1) % N;       // start inside edge -> next full vertex
        pts.push(cp(verts[curV]));
      }

      while (curV !== to.edge) {
        const nextV = (curV + 1) % N;
        if (!eq(pts[pts.length-1], verts[nextV])) pts.push(cp(verts[nextV]));
        curV = nextV;
        if (pts.length > N + 5) break;    // safety
      }

      if (to.atVertex !== -1) {
        const vIdx = to.atVertex;
        if (!eq(pts[pts.length-1], verts[vIdx])) pts.push(cp(verts[vIdx]));
      } else {
        if (!eq(pts[pts.length-1], to.point)) pts.push(cp(to.point));
      }

      // dedupe neighbours
      const out = [];
      for (const p of pts) if (!out.length || !eq(out[out.length-1], p)) out.push(p);
      if (out.length>1 && eq(out[0], out[out.length-1])) out.pop();
      return out;
    }

    // ---- locate A and B robustly ----
    const A = locateOnBoundary(pA, "pA");
    const B = locateOnBoundary(pB, "pB");

    // ---- build two point rings (open; you will close them when building lines) ----
    const pathAB = walkForward(A, B);
    const pathBA = walkForward(B, A);

    if (debug) {
      console.debug("pathAB", pathAB);
      console.debug("pathBA", pathBA);
    }

    return [pathAB, pathBA];
  }
}

export default SeamTool
